import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// KTK-218 — workspace spend dashboard.
//
// Shows the workspace's accrued spend at three granularities
// (workspace / machine / agent) using rocky's GET /api/usage/*
// endpoints, plus the cap-config form (GET/PUT /api/workspaces/:id/
// spend-cap). Lives inside the workspace settings dialog as a new
// section but is self-contained so any other surface that wants the
// dashboard can mount it directly.
//
// KTK-386 — range presets grew rolling 30/60-day windows (sent as
// from/to; enum periods still server-computed UTC), a daily
// spend-over-time chart, and proportional cost bars in the breakdown
// tables. Rolling results are trusted only when the Hub echoes the
// resolved range back — an older Hub ignores from/to and answers with
// default-period data, which must not be labeled "past 30 days".
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDataContext } from "../../hub/data-context-provider.js";
import { SpendOverTimeChart } from "./SpendOverTimeChart.js";
import { useThemeMode } from "../useThemeMode.js";
/** Inclusive rolling window of the last `days` UTC days, ending today. */
function rollingRange(days) {
    const DAY = 86_400_000;
    const to = new Date();
    const from = new Date(to.getTime() - (days - 1) * DAY);
    const fmt = (d) => d.toISOString().slice(0, 10);
    return { from: fmt(from), to: fmt(to) };
}
export function SpendDashboard({ workspaceId }) {
    const ctx = useDataContext();
    const themeMode = useThemeMode();
    const [rangeKey, setRangeKey] = useState("month");
    const [usage, setUsage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [cap, setCap] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [savingCap, setSavingCap] = useState(false);
    const [dailyCapInput, setDailyCapInput] = useState("");
    const [monthlyCapInput, setMonthlyCapInput] = useState("");
    const fetchUsage = useCallback(async () => {
        setLoadError(null);
        setLoading(true);
        const hub = ctx.hub;
        if (typeof hub.getWorkspaceUsage !== "function") {
            setLoadError("Workspace usage endpoints are not available on this Hub yet.");
            setLoading(false);
            return;
        }
        const isRolling = rangeKey === "30d" || rangeKey === "60d";
        const query = isRolling ? rollingRange(rangeKey === "30d" ? 30 : 60) : rangeKey;
        try {
            const [u, c] = await Promise.all([
                hub.getWorkspaceUsage(workspaceId, query),
                hub.getWorkspaceSpendCap?.(workspaceId) ?? Promise.resolve({
                    daily_cap_usd: null,
                    monthly_cap_usd: null,
                }),
            ]);
            // A Hub predating from/to ranges ignores them and answers with its
            // default period. The post-386 Hub echoes `period: "custom"` for a
            // range query; anything else means unsupported — refuse to present
            // that as a rolling window rather than silently mislabel it.
            if (isRolling && u.period !== "custom") {
                setUsage(null);
                setLoadError("Rolling ranges aren't available yet — use Today, Month or All.");
                return;
            }
            setUsage(u);
            setCap(c);
            setDailyCapInput(c.daily_cap_usd != null ? c.daily_cap_usd.toString() : "");
            setMonthlyCapInput(c.monthly_cap_usd != null ? c.monthly_cap_usd.toString() : "");
        }
        catch (err) {
            setLoadError(err instanceof Error ? err.message : String(err));
        }
        finally {
            setLoading(false);
        }
    }, [ctx.hub, workspaceId, rangeKey]);
    useEffect(() => {
        void fetchUsage();
    }, [fetchUsage]);
    const saveCap = useCallback(async () => {
        setSavingCap(true);
        try {
            const hub = ctx.hub;
            if (typeof hub.setWorkspaceSpendCap !== "function") {
                setLoadError("setWorkspaceSpendCap not available on this Hub yet.");
                return;
            }
            const next = await hub.setWorkspaceSpendCap(workspaceId, {
                daily_cap_usd: parseCapInput(dailyCapInput),
                monthly_cap_usd: parseCapInput(monthlyCapInput),
            });
            setCap(next);
        }
        catch (err) {
            setLoadError(err instanceof Error ? err.message : String(err));
        }
        finally {
            setSavingCap(false);
        }
    }, [ctx.hub, workspaceId, dailyCapInput, monthlyCapInput]);
    const periodLabel = useMemo(() => {
        switch (rangeKey) {
            case "today":
                return "Today (UTC)";
            case "30d":
                return "Past 30 days (UTC)";
            case "60d":
                return "Past 60 days (UTC)";
            case "month":
                return "This month (UTC)";
            case "all":
                return "All time";
        }
    }, [rangeKey]);
    return (
    // KTK-386 — follows the app theme instead of pinning dark: the
    // dashboard sits inside the (theme-following) settings dialog, where a
    // pinned-dark island is the KTK-384 bug shape all over again.
    _jsxs("div", { "data-agent-v2": "", "data-theme": themeMode, style: { display: "flex", flexDirection: "column", gap: 14 }, children: [_jsxs("div", { style: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }, children: [_jsx("h3", { style: { font: "inherit", fontSize: 14, fontWeight: 600, color: "var(--ink)" }, children: "Spend" }), _jsx(PeriodSegment, { value: rangeKey, onChange: setRangeKey }), _jsx("span", { style: { flex: 1 } }), _jsx("button", { type: "button", onClick: () => void fetchUsage(), style: ghostBtn(), children: "refresh" })] }), loadError && (_jsx("div", { style: {
                    padding: "8px 10px",
                    border: "1px solid color-mix(in oklab, var(--accent) 45%, transparent)",
                    borderRadius: "var(--radius-sm)",
                    background: "var(--accent-soft)",
                    color: "var(--accent-ink)",
                    fontSize: "var(--fs-meta)",
                }, children: loadError })), usage && (
            // Refetch keeps the frame: previous render holds at reduced
            // opacity while a new range loads — no skeleton, no layout jump.
            _jsxs("div", { style: { display: "flex", flexDirection: "column", gap: 14, opacity: loading ? 0.55 : 1, transition: "opacity 120ms ease" }, children: [_jsx(SummaryCard, { usage: usage, periodLabel: periodLabel }), _jsxs("div", { style: {
                            padding: "10px 12px 4px",
                            border: "1px solid var(--hairline)",
                            borderRadius: "var(--radius-sm)",
                            background: "var(--paper-2)",
                        }, children: [_jsx("div", { style: { fontSize: "var(--fs-meta)", color: "var(--ink-3)", marginBottom: 4 }, children: "Daily spend" }), usage.by_day ? (_jsx(SpendOverTimeChart, { days: usage.by_day })) : (_jsx("div", { style: { padding: "6px 0 12px", fontSize: "var(--fs-meta)", color: "var(--ink-3)" }, children: "Daily breakdown isn't available yet \u2014 totals above are unaffected." }))] }), usage.by_model && usage.by_model.length > 0 && (_jsx(BreakdownTable, { title: "By model", rows: usage.by_model })), usage.by_host && usage.by_host.length > 0 && (_jsx(BreakdownTable, { title: "By machine", rows: usage.by_host })), usage.by_agent && usage.by_agent.length > 0 && (_jsx(BreakdownTable, { title: "By agent", rows: usage.by_agent }))] })), _jsxs("div", { style: {
                    marginTop: 6,
                    padding: 12,
                    border: "1px solid var(--hairline)",
                    borderRadius: "var(--radius-sm)",
                    background: "var(--paper-2)",
                }, children: [_jsx("div", { style: { fontSize: "var(--fs-meta)", color: "var(--ink-3)", marginBottom: 6 }, children: "Caps" }), _jsxs("div", { style: { display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }, children: [_jsx(CapInput, { label: "Daily cap (USD)", value: dailyCapInput, onChange: setDailyCapInput, spent: cap?.day_spent_usd }), _jsx(CapInput, { label: "Monthly cap (USD)", value: monthlyCapInput, onChange: setMonthlyCapInput, spent: cap?.month_spent_usd }), _jsx("button", { type: "button", onClick: () => void saveCap(), disabled: savingCap, style: primaryBtn(savingCap), children: savingCap ? "saving…" : "save caps" })] }), _jsxs("div", { style: { fontSize: 11, color: "var(--ink-4)", marginTop: 6, fontStyle: "italic" }, children: ["Cap of ", _jsx("code", { children: "0" }), " or empty means no cap. Boundaries are UTC."] })] })] }));
}
function SummaryCard({ usage, periodLabel }) {
    const promptIn = safeNum(usage.prompt_tokens);
    const cached = safeNum(usage.cached_input_tokens);
    const cachePct = promptIn > 0 ? Math.round((cached / promptIn) * 100) : 0;
    return (_jsxs("div", { style: {
            display: "grid",
            // KTK-227 — bumped to 5 cols to add a Cached-input stat
            // alongside the existing cost / turns / tokens / reasoning.
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: 10,
            padding: 12,
            border: "1px solid var(--hairline)",
            borderRadius: "var(--radius-sm)",
            background: "var(--paper-2)",
        }, children: [_jsx(Stat, { label: "Cost", value: `$${safeNum(usage.cost_usd).toFixed(4)}`, sub: periodLabel }), _jsx(Stat, { label: "Turns", value: safeNum(usage.turns).toLocaleString() }), _jsx(Stat, { label: "Tokens (in/out)", value: `${promptIn.toLocaleString()} / ${safeNum(usage.completion_tokens).toLocaleString()}` }), _jsx(Stat, { label: "Cached input", value: cached.toLocaleString(), sub: cached > 0 ? `${cachePct}% of input` : "no cache hits" }), _jsx(Stat, { label: "Reasoning tokens", value: safeNum(usage.reasoning_tokens).toLocaleString() })] }));
}
function Stat({ label, value, sub }) {
    return (_jsxs("div", { style: { display: "flex", flexDirection: "column", gap: 2 }, children: [_jsx("span", { style: { fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.04 }, children: label }), _jsx("span", { style: { fontSize: 18, fontFamily: "var(--mono)", color: "var(--ink)" }, children: value }), sub && _jsx("span", { style: { fontSize: 11, color: "var(--ink-4)" }, children: sub })] }));
}
function BreakdownTable({ title, rows }) {
    // Pre-sort by cost descending so the most expensive surface lands
    // first — keeps the most likely "what's costing me money?" answer
    // at the top of the table without a sort UI in v1.
    const sorted = [...rows].sort((a, b) => safeNum(b.cost_usd) - safeNum(a.cost_usd));
    return (_jsxs("div", { children: [_jsx("div", { style: { fontSize: "var(--fs-meta)", color: "var(--ink-3)", marginBottom: 4 }, children: title }), _jsxs("div", { style: {
                    border: "1px solid var(--hairline)",
                    borderRadius: "var(--radius-sm)",
                    overflow: "hidden",
                }, children: [_jsxs("div", { style: {
                            display: "grid",
                            // name, cost, turns, tokens, cached% — KTK-227 added the
                            // last column so each row shows its cache-hit ratio.
                            gridTemplateColumns: "minmax(0, 2fr) repeat(4, minmax(0, 1fr))",
                            padding: "6px 10px",
                            background: "var(--paper-3)",
                            fontSize: 11,
                            color: "var(--ink-3)",
                            textTransform: "uppercase",
                            letterSpacing: 0.04,
                        }, children: [_jsx("span", { children: "name" }), _jsx("span", { style: { textAlign: "right" }, children: "cost" }), _jsx("span", { style: { textAlign: "right" }, children: "turns" }), _jsx("span", { style: { textAlign: "right" }, children: "tokens" }), _jsx("span", { style: { textAlign: "right" }, children: "cached" })] }), sorted.map((r) => {
                        const promptIn = safeNum(r.prompt_tokens);
                        const cached = safeNum(r.cached_input_tokens);
                        const pct = promptIn > 0 ? Math.round((cached / promptIn) * 100) : 0;
                        const maxCost = safeNum(sorted[0]?.cost_usd);
                        const share = maxCost > 0 ? safeNum(r.cost_usd) / maxCost : 0;
                        return (_jsxs("div", { style: {
                                display: "grid",
                                gridTemplateColumns: "minmax(0, 2fr) repeat(4, minmax(0, 1fr))",
                                padding: "8px 10px",
                                borderTop: "1px solid var(--hairline-2)",
                                fontSize: "var(--fs-body)",
                                color: "var(--ink)",
                            }, children: [_jsxs("span", { style: { minWidth: 0 }, children: [_jsx("span", { style: { display: "block", fontFamily: r.label ? "inherit" : "var(--mono)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }, children: r.label ?? r.key }), _jsx("span", { style: {
                                                display: "block",
                                                marginTop: 4,
                                                marginRight: 14,
                                                height: 4,
                                                borderRadius: 2,
                                                background: "var(--paper-3)",
                                                overflow: "hidden",
                                            }, "aria-hidden": "true", children: _jsx("span", { style: {
                                                    display: "block",
                                                    width: `${Math.max(share * 100, safeNum(r.cost_usd) > 0 ? 2 : 0)}%`,
                                                    height: "100%",
                                                    borderRadius: 2,
                                                    background: "var(--chart-mark)",
                                                } }) })] }), _jsxs("span", { style: { textAlign: "right", fontFamily: "var(--mono)" }, children: ["$", safeNum(r.cost_usd).toFixed(4)] }), _jsx("span", { style: { textAlign: "right", fontFamily: "var(--mono)" }, children: safeNum(r.turns).toLocaleString() }), _jsx("span", { style: { textAlign: "right", fontFamily: "var(--mono)" }, children: (promptIn + safeNum(r.completion_tokens)).toLocaleString() }), _jsx("span", { style: { textAlign: "right", fontFamily: "var(--mono)", color: cached > 0 ? "var(--ink)" : "var(--ink-4)" }, title: cached > 0 ? `${cached.toLocaleString()} of ${promptIn.toLocaleString()} input tokens served from cache` : "no cache hits", children: cached > 0 ? `${pct}%` : "—" })] }, r.key));
                    })] })] }));
}
function PeriodSegment({ value, onChange }) {
    const opts = [
        { id: "today", label: "Today" },
        { id: "30d", label: "30 days" },
        { id: "60d", label: "60 days" },
        { id: "month", label: "Month" },
        { id: "all", label: "All" },
    ];
    return (_jsx("div", { style: {
            display: "inline-flex",
            background: "var(--paper-2)",
            border: "1px solid var(--hairline)",
            borderRadius: 999,
            padding: 2,
        }, children: opts.map((o) => (_jsx("button", { type: "button", onClick: () => onChange(o.id), style: {
                font: "inherit",
                fontSize: 12,
                padding: "3px 12px",
                border: "none",
                borderRadius: 999,
                cursor: "pointer",
                background: value === o.id ? "var(--paper)" : "transparent",
                color: value === o.id ? "var(--ink)" : "var(--ink-3)",
                fontWeight: value === o.id ? 600 : 500,
            }, children: o.label }, o.id))) }));
}
function CapInput({ label, value, onChange, spent, }) {
    const num = parseFloat(value);
    const exceeded = !Number.isNaN(num) && num > 0 && spent != null && spent >= num;
    return (_jsxs("label", { style: { display: "flex", flexDirection: "column", gap: 4, minWidth: 160 }, children: [_jsx("span", { style: { fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.04 }, children: label }), _jsx("input", { type: "number", min: 0, step: 0.01, value: value, onChange: (e) => onChange(e.target.value), placeholder: "0", style: {
                    padding: "6px 10px",
                    border: `1px solid ${exceeded ? "var(--accent)" : "var(--hairline)"}`,
                    borderRadius: "var(--radius-sm)",
                    background: "var(--paper)",
                    color: "var(--ink)",
                    font: "inherit",
                    fontFamily: "var(--mono)",
                    fontSize: "var(--fs-body)",
                    width: 140,
                } }), spent != null && (_jsxs("span", { style: { fontSize: 11, color: exceeded ? "var(--accent-ink)" : "var(--ink-4)" }, children: ["spent: $", safeNum(spent).toFixed(4)] }))] }));
}
function parseCapInput(s) {
    const trimmed = s.trim();
    if (!trimmed)
        return null;
    const n = parseFloat(trimmed);
    if (!Number.isFinite(n) || n <= 0)
        return null;
    return n;
}
function ghostBtn() {
    return {
        font: "inherit",
        fontSize: "var(--fs-meta)",
        padding: "4px 10px",
        borderRadius: 999,
        border: "1px solid var(--hairline)",
        background: "var(--paper-2)",
        color: "var(--ink-2)",
        cursor: "pointer",
    };
}
function primaryBtn(disabled = false) {
    return {
        font: "inherit",
        fontSize: "var(--fs-meta)",
        padding: "6px 14px",
        borderRadius: 999,
        border: "1px solid var(--accent)",
        background: "var(--accent)",
        color: "var(--paper)",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
    };
}
// Hub may return usage rows with undefined numeric fields when no data
// has accrued yet (or for periods with no turns) — coerce to 0 so the
// downstream .toFixed / .toLocaleString calls don't throw and blank
// the workspace settings dialog.
function safeNum(v) {
    return typeof v === "number" && Number.isFinite(v) ? v : 0;
}
//# sourceMappingURL=SpendDashboard.js.map